import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Heading, Spinner, Text, XStack, YStack } from "tamagui";

import {
  WeatherExternalAirQualityCard,
  WeatherExternalHumidityCard,
  WeatherForecastCard,
  WeatherHighLowCard,
  WeatherUvIndexCard,
} from "../components/WeatherCard";
import { useGetWeatherData } from "../hooks/weather/useGetWeatherData";
import { WeatherData } from "../types/Weather";

const Weather = () => {
  const {
    getWeather,
    currentLocation,
    isLoading: isLocationLoading,
  } = useGetWeatherData();
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [isWeatherLoading, setIsWeatherLoading] = useState(true);
  const { t } = useTranslation();

  const updateWeatherData = async () => {
    if (currentLocation) {
      setIsWeatherLoading(true);
      const data = await getWeather();
      setWeatherData(data);
      setIsWeatherLoading(false);
    }
  };

  useEffect(() => {
    if (
      currentLocation &&
      (!weatherData ||
        JSON.stringify(currentLocation) !== lastFetchedLocation.current)
    ) {
      lastFetchedLocation.current = JSON.stringify(currentLocation);
      updateWeatherData();
    }
  }, [currentLocation]);

  const lastFetchedLocation = useRef("");

  if (
    isLocationLoading ||
    isWeatherLoading ||
    !weatherData ||
    !currentLocation
  ) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Spinner size="large" />
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <YStack gap="$3">
        <Heading fontSize="$8" lineHeight="$6">
          {t("weather.title")}
        </Heading>
        <Text fontSize="$7.5" color="$white10">
          {currentLocation.name}
        </Text>
      </YStack>

      <XStack gap="$5">
        <WeatherHighLowCard
          value={weatherData.current.temperature}
          unit={weatherData.current.temperatureUnit}
          conditions={weatherData.current.conditions}
          high={weatherData.current.highTemperature}
          low={weatherData.current.lowTemperature}
        />
        <WeatherForecastCard
          value={weatherData.upcoming.temperature}
          unit={weatherData.upcoming.temperatureUnit}
          conditions={weatherData.upcoming.conditions}
          forecast={weatherData.upcoming.forecast}
        />
      </XStack>

      <XStack gap="$5">
        <WeatherUvIndexCard
          minHeight={269}
          value={weatherData.current.uvIndex}
          level={weatherData.current.uvIndexLevel}
        />
        <WeatherExternalAirQualityCard
          minHeight={269}
          value={weatherData.current.airQuality}
          level={weatherData.current.airQualityLevel}
        />
      </XStack>

      <XStack gap="$5">
        <WeatherExternalHumidityCard
          minHeight={269}
          value={weatherData.current.relativeHumidity}
          level={weatherData.current.relativeHumidityLevel}
          forecast={weatherData.upcoming.forecast}
        />
        <div style={{ flexGrow: 1 }} />
      </XStack>
    </YStack>
  );
};

export default Weather;
